import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GetStartedButton } from '../common/GetStartedButton';
import { SegmentedToggle } from '../common/SegmentedToggle';
import { WhoItsForBox } from './WhoItsForBox';
import { leaderboardLink } from '../../utils/profileLinks';
import api from '../../services/api';

// Stat keys match the backend's /public/top-players list (the same stats
// coaches rank by on /coach/statistics).
export const CATEGORIES = {
  batting: {
    label: 'Hitting',
    metrics: [
      { value: 'batting_average', label: 'AVG', name: 'Batting Average', format: 'rate' },
      { value: 'on_base_percentage', label: 'OBP', name: 'On-Base Percentage', format: 'rate' },
      { value: 'slugging_percentage', label: 'SLG', name: 'Slugging Percentage', format: 'rate' },
      { value: 'home_runs', label: 'HR', name: 'Home Runs', format: 'int' },
      { value: 'rbi', label: 'RBI', name: 'Runs Batted In', format: 'int' },
      { value: 'hits', label: 'H', name: 'Hits', format: 'int' },
      { value: 'runs', label: 'R', name: 'Runs', format: 'int' },
      { value: 'stolen_bases', label: 'SB', name: 'Stolen Bases', format: 'int' },
      { value: 'walks', label: 'BB', name: 'Walks', format: 'int' },
    ],
  },
  pitching: {
    label: 'Pitching',
    metrics: [
      { value: 'era', label: 'ERA', name: 'Earned Run Average', format: 'era', ascending: true },
      { value: 'strikeouts_pitched', label: 'K', name: 'Strikeouts', format: 'int' },
      { value: 'wins', label: 'W', name: 'Wins', format: 'int' },
      { value: 'saves', label: 'SV', name: 'Saves', format: 'int' },
      { value: 'innings_pitched', label: 'IP', name: 'Innings Pitched', format: 'ip' },
      { value: 'complete_games', label: 'CG', name: 'Complete Games', format: 'int' },
      { value: 'shutouts', label: 'SHO', name: 'Shutouts', format: 'int' },
    ],
  },
};

export const formatStat = (value, format) => {
  if (value === null || value === undefined) return '-';
  if (format === 'rate') return value.toFixed(3).replace(/^0/, '');
  if (format === 'era') return value.toFixed(2);
  if (format === 'ip') return value.toFixed(1);
  return value;
};

// School logo when the player has one (teamLogo, from the team's logo field);
// otherwise a badge with the school's initials.
// onBand: the large sizes sit on the navy header, where a theme-colored outline
// would blend into the navy in dark mode and make the circle look smaller. They
// get no outline, so the circle is the same size in light and dark.
const LOGO_SIZES = {
  sm: { box: 'w-8 h-8', text: 'text-[10px]', onBand: false },
  lg: { box: 'w-16 h-16', text: 'text-lg', onBand: true },
  xl: { box: 'w-24 h-24 sm:w-28 sm:h-28', text: 'text-2xl sm:text-3xl', onBand: true },
};

export const TeamLogo = ({ team, logo, size: sizeName = 'sm' }) => {
  const [failed, setFailed] = useState(false);
  const { box, text, onBand } = LOGO_SIZES[sizeName];
  const size = `${box} rounded-full flex-shrink-0`;

  if (logo && !failed) {
    return (
      <img
        src={logo}
        alt={`${team} logo`}
        onError={() => setFailed(true)}
        className={`${size} object-contain bg-white ${onBand ? '' : 'border border-jp-line'}`}
      />
    );
  }

  const initials = team
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('');

  return (
    <span
      aria-hidden="true"
      title={team}
      className={`${size} flex items-center justify-center font-bold ${text} ${
        onBand ? 'bg-white text-jp-band' : 'bg-jp-section border border-jp-outline/50 text-jp-muted'
      }`}
    >
      {initials}
    </span>
  );
};

// Opens the profile in the app for members. Everyone else gets the blurred
// player or team preview, built from this row only.
const ProfileLink = ({ target, preview, label, className = '', children }) => {
  const user = useSelector((state) => state.auth.user);
  const { to, state } = leaderboardLink(user, target, preview);

  return (
    <Link
      to={to}
      state={state}
      aria-label={label}
      className={`group/link rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent ${className}`}
    >
      {children}
    </Link>
  );
};

// 2025 -> "2025–26"
const seasonLabel = (season) => `${season}–${String((season + 1) % 100).padStart(2, '0')}`;

// Division filter; values match the backend's /public/top-players `division`.
const DIVISIONS = ['NJCAA D1', 'NJCAA D2', 'NJCAA D3', '3C2A'];
const ALL_DIVISIONS = '';

// Labelled pill dropdown used for "Sort by" and "Division".
const LeaderboardSelect = ({ label, value, onChange, children }) => (
  <label className="relative flex items-center gap-2">
    <span className="text-xs font-bold uppercase tracking-wider text-jp-muted">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none bg-none cursor-pointer rounded-full bg-jp-section border border-jp-outline/70 hover:border-jp-accent/60 pl-4 pr-9 py-2 text-xs font-bold text-jp-accent-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
    >
      {children}
    </select>
    <svg className="pointer-events-none absolute right-3 w-3.5 h-3.5 text-jp-accent-ink" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </label>
);

// Rank, player (name + position), school (logo + name), stat.
const ROW_COLUMNS = 'grid-cols-[2rem_minmax(0,1fr)_minmax(0,1fr)_auto]';

const rankBadgeClass = (rank) => {
  if (rank === 1) return 'bg-jp-gold text-jp-navy';
  if (rank <= 3) return 'bg-jp-ink/10 text-jp-ink';
  return 'bg-jp-section text-jp-muted';
};

const LeaderboardWithAudience = () => {
  const [category, setCategory] = useState('batting');
  const [metricValue, setMetricValue] = useState(CATEGORIES.batting.metrics[0].value);
  const [division, setDivision] = useState(ALL_DIVISIONS);

  const metric = CATEGORIES[category].metrics.find((m) => m.value === metricValue);

  // Real top 10 for the chosen stat, from the public (no-login) endpoint
  const [leaders, setLeaders] = useState([]);
  const [season, setSeason] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api
      .get('/public/top-players', { params: { category, metric: metricValue, division: division || undefined } })
      .then(({ data }) => {
        if (cancelled) return;
        setLeaders(data.players || []);
        setSeason(data.season ? Number(data.season) : null);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setLeaders([]);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [category, metricValue, division]);

  const handleCategoryChange = (next) => {
    setCategory(next);
    setMetricValue(CATEGORIES[next].metrics[0].value);
  };

  return (
    <section className="bg-jp-section border-y border-jp-line px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
        <WhoItsForBox />
        <div className="w-full lg:w-[28rem] flex-shrink-0 flex flex-col">
          {/* Section Header */}
          <div className="mb-5">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink">
              Top JUCO <span className="text-jp-accent">Performers</span>
            </h2>
            {season && (
              <p className="mt-1 text-sm text-jp-muted">
                {seasonLabel(season)} season
              </p>
            )}
          </div>

          <div className="flex-1 bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4">
            {/* Hitting / Pitching toggle, stat picker, and division filter */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <SegmentedToggle
                ariaLabel="Stat category"
                options={Object.entries(CATEGORIES).map(([key, cat]) => ({ value: key, label: cat.label }))}
                value={category}
                onChange={handleCategoryChange}
              />
              <LeaderboardSelect label="Sort by" value={metricValue} onChange={setMetricValue}>
                {CATEGORIES[category].metrics.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label} · {m.name}
                  </option>
                ))}
              </LeaderboardSelect>
              <LeaderboardSelect label="Division" value={division} onChange={setDivision}>
                <option value={ALL_DIVISIONS}>All Divisions</option>
                {DIVISIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </LeaderboardSelect>
            </div>

            {/* Leaders table */}
            <div className="divide-y divide-jp-line">
              <div className={`grid ${ROW_COLUMNS} gap-3 px-2 pb-2 text-xs font-bold uppercase tracking-wider text-jp-muted`}>
                <span>#</span>
                <span>Player</span>
                <span>School</span>
                <span className="text-right" title={metric.name}>{metric.label}</span>
              </div>
              {status === 'loading' &&
                Array.from({ length: 10 }, (_, i) => (
                  <div key={i} className={`grid ${ROW_COLUMNS} gap-3 items-center px-2 py-2`} aria-hidden="true">
                    <span className="w-6 h-6 rounded-full bg-jp-section animate-pulse" />
                    <span className="h-3.5 rounded bg-jp-section animate-pulse" />
                    <span className="h-3.5 rounded bg-jp-section animate-pulse" />
                    <span className="h-3.5 w-10 rounded bg-jp-section animate-pulse" />
                  </div>
                ))}
              {status !== 'loading' && leaders.length === 0 && (
                <p className="px-2 py-10 text-center text-sm text-jp-muted">
                  The leaderboard is updating. Check back soon.
                </p>
              )}
              {status === 'ready' && leaders.map((player, index) => (
                <div
                  key={player.id || player.name}
                  className={`grid ${ROW_COLUMNS} gap-3 items-center px-2 py-2 hover:bg-jp-section rounded-lg transition-colors ${index === 0 ? 'dark:bg-jp-gold/10' : ''}`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${rankBadgeClass(index + 1)}`}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <ProfileLink
                      target={{ kind: 'player', id: player.id }}
                      // Only what this row shows; the preview page asks the server for nothing
                      preview={{
                        player: {
                          id: player.id,
                          name: player.name,
                          position: player.position,
                          jerseyNumber: player.jerseyNumber,
                          profileImage: player.profileImage,
                          team: player.team,
                          teamId: player.teamId,
                          teamLogo: player.teamLogo,
                        },
                        category,
                        division: division || null,
                        stat: {
                          rank: index + 1,
                          label: metric.label,
                          value: formatStat(player[metric.value], metric.format),
                          season: season ? seasonLabel(season) : null,
                        },
                      }}
                      label={`View ${player.name}'s profile`}
                      className="block"
                    >
                      <p className="text-jp-ink text-sm font-semibold truncate transition-colors group-hover/link:text-jp-accent-ink group-hover/link:underline">
                        {player.name}
                      </p>
                    </ProfileLink>
                    <p className="text-jp-muted text-xs truncate">{player.position}</p>
                  </div>
                  <ProfileLink
                    target={{ kind: 'team', id: player.teamId }}
                    preview={{
                      team: { id: player.teamId, name: player.team, logo: player.teamLogo },
                      division: division || null,
                    }}
                    label={`View ${player.team} team profile`}
                    className="min-w-0 flex items-center gap-2"
                  >
                    <TeamLogo team={player.team} logo={player.teamLogo} />
                    <span className="text-jp-muted text-xs truncate transition-colors group-hover/link:text-jp-accent-ink group-hover/link:underline">
                      {player.team}
                    </span>
                  </ProfileLink>
                  <span className={`text-right text-base font-bold tabular-nums text-jp-accent-ink ${index === 0 ? 'dark:text-jp-gold' : ''}`}>
                    {formatStat(player[metric.value], metric.format)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end mt-5">
            <GetStartedButton>SEE EVERY PLAYER</GetStartedButton>
          </div>
        </div>
      </div>
    </section>
  );
};

// Switch off to show only "Who It's For" (e.g. if /public/top-players is down).
const SHOW_LEADERBOARD = true;

export const LeaderboardSection = () =>
  SHOW_LEADERBOARD ? (
    <LeaderboardWithAudience />
  ) : (
    <section className="bg-jp-section border-y border-jp-line px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="max-w-7xl mx-auto">
        <WhoItsForBox />
      </div>
    </section>
  );

export default LeaderboardSection;
